/**
 * Utility function to dynamically load JavaScript from CDN
 * @param url - The CDN URL to load
 * @param globalVars - Array of global variable names to check for successful load
 * @param attrs - HTML attributes to set on the injected <script> tag (see the note on
 *                document.body below for why these only work from <body>)
 * @returns Promise that resolves with the loaded global objects
 */

// Cache the in-flight CDN load promise so concurrent calls to connectSocket()
// (e.g. socketMiddleware fires at 100ms AND AppLayout fires at 1s, both before
// window.io is defined) share a single <script> tag injection instead of each
// adding their own. Without this, each call adds a duplicate tag, causing the
// browser to warn: "preloaded using link preload but not used".
let cdnLoadPromise: Promise<any[]> | null = null;

export const fetchJsFromCDN = (url: string, globalVars: string[], attrs: Record<string, string> = {}): Promise<any[]> => {
    // If all globals are already defined, resolve immediately without touching the DOM
    const allLoaded = globalVars.every((varName) => (window as any)[varName]);

    if (allLoaded) {
        return Promise.resolve(globalVars.map((varName) => (window as any)[varName]));
    }

    // Reuse in-flight promise — only one <script> tag is ever injected per session
    if (cdnLoadPromise) return cdnLoadPromise;

    cdnLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = url;
        script.async = true;
        // crossOrigin must match the <link rel="preload" crossorigin="anonymous"> in index.html
        // so the browser serves the preloaded copy from cache instead of issuing a duplicate
        // network request and emitting the "preloaded but not used" warning.
        script.crossOrigin = 'anonymous';

        // Boot config for sails.io.js is read from HTML attributes on the script tag (autoConnect,
        // environment, url, headers, transports, path). It must be set here rather than on io.sails
        // afterwards, because sails.io.js auto-connects on a setTimeout(0) that fires long before
        // our onload handler resolves.
        Object.entries(attrs).forEach(([key, value]) => script.setAttribute(key, value));

        script.onload = () => {
            // Brief wait for the library to finish initializing its globals (sails.io.js pattern)
            setTimeout(() => {
                const loadedVars = globalVars.map((varName) => (window as any)[varName]);
                const allPresent = loadedVars.every((v) => v !== undefined);

                if (allPresent) {
                    resolve(loadedVars);
                } else {
                    // Reset so a future call can retry after a transient failure
                    cdnLoadPromise = null;
                    reject(new Error(`Failed to load global variables: ${globalVars.join(', ')}`));
                }
            }, 50);
        };

        script.onerror = () => {
            // Reset so a future call can retry
            cdnLoadPromise = null;
            reject(new Error(`Failed to load script from ${url}`));
        };

        // MUST be <body>, not <head>. sails.io.js identifies "its own" script tag as the LAST
        // <script> in the document (not document.currentScript), and CRA places the app bundle in
        // <body> — so a tag appended to <head> is not last and every attribute above is silently
        // read off the bundle tag instead. document.body is guaranteed here: this only runs from
        // connectSocket(), well after React has mounted.
        document.body.appendChild(script);
    });

    return cdnLoadPromise;
};
